import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  X, AlertTriangle, CheckCircle, Trash2, Loader2, ChevronDown, ChevronUp,
  Users, Baby, Calendar, Hash, ShieldAlert, Filter
} from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface DuplicateKid {
  id: string
  nom: string
  prenom: string
  date_naissance: string
  n_national?: string
  created_at: string
  user_id: string
  parentLabel: string
  registrations_count: number
  canDelete: boolean
}

interface DuplicateGroup {
  key: string
  matchReasons: string[]
  parentName: string
  kids: DuplicateKid[]
}

interface Filters {
  prenom: boolean
  dateNaissance: boolean
  nNational: boolean
}

interface Props {
  onClose: () => void
  onDeleted: () => void
}

function normalize(s: string) {
  return s.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('fr-FR')
}

function formatDateTime(d: string) {
  return new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function computeGroups(kids: DuplicateKid[], filters: Filters): DuplicateGroup[] {
  const groups: DuplicateGroup[] = []

  // Group kids by user_id first
  const byUser = new Map<string, DuplicateKid[]>()
  for (const kid of kids) {
    if (!byUser.has(kid.user_id)) byUser.set(kid.user_id, [])
    byUser.get(kid.user_id)!.push(kid)
  }

  for (const [userId, userKids] of byUser) {
    if (userKids.length < 2) continue

    // Build signature from the always-present fields (prenom, date_naissance)
    // n_national is used as an extra matching reason but doesn't split groups
    const sigMap = new Map<string, DuplicateKid[]>()

    for (const kid of userKids) {
      const sigParts: string[] = []

      if (filters.prenom) sigParts.push(`p:${normalize(kid.prenom)}`)
      if (filters.dateNaissance) sigParts.push(`d:${kid.date_naissance}`)

      if (sigParts.length === 0) {
        // Only n_national filter is active — use it as the grouping key
        if (filters.nNational && kid.n_national && kid.n_national.trim() !== '') {
          sigParts.push(`n:${kid.n_national.trim()}`)
        } else {
          continue
        }
      }

      const sig = sigParts.join('|')
      if (!sigMap.has(sig)) sigMap.set(sig, [])
      sigMap.get(sig)!.push(kid)
    }

    for (const [sig, groupKids] of sigMap) {
      if (groupKids.length < 2) continue
      if (!groupKids.some(k => k.canDelete)) continue

      // Determine which reasons apply within this group
      const reasons: string[] = []
      if (filters.prenom) reasons.push('Prénom identique')
      if (filters.dateNaissance) reasons.push('Date de naissance identique')

      // Check if n_national matches within the group (at least 2 kids share the same)
      if (filters.nNational) {
        const nNationals = groupKids
          .map(k => k.n_national?.trim())
          .filter(n => n && n !== '')
        const nSet = new Set(nNationals)
        if (nNationals.length > nSet.size || (nSet.size === 1 && nNationals.length >= 2)) {
          reasons.push('Numéro national identique')
        }
      }

      groups.push({
        key: `${userId}:${sig}`,
        matchReasons: reasons,
        parentName: groupKids[0].parentLabel,
        kids: groupKids.sort((a, b) => a.created_at.localeCompare(b.created_at)),
      })
    }
  }

  // Sort: most reasons first, then by parent name
  groups.sort((a, b) => b.matchReasons.length - a.matchReasons.length || a.parentName.localeCompare(b.parentName))

  return groups
}

function keeperOf(group: DuplicateGroup): string {
  const withRegs = group.kids.filter(k => k.registrations_count > 0)
  return withRegs.length > 0 ? withRegs[0].id : group.kids[0].id
}

const REASON_COLOR: Record<string, string> = {
  'Numéro national identique': 'bg-red-100 text-red-700',
  'Date de naissance identique': 'bg-amber-100 text-amber-700',
  'Prénom identique': 'bg-blue-100 text-blue-700',
}

const CHUNK_SIZE = 1000

export function DuplicateKidsPanel({ onClose, onDeleted }: Props) {
  const [allKids, setAllKids] = useState<DuplicateKid[]>([])
  const [loading, setLoading] = useState(true)
  const [loadedCount, setLoadedCount] = useState(0)
  const [filters, setFilters] = useState<Filters>({ prenom: true, dateNaissance: true, nNational: true })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [deletedCount, setDeletedCount] = useState<number | null>(null)

  const groups = useMemo(() => computeGroups(allKids, filters), [allKids, filters])

  const loadKids = useCallback(async () => {
    setLoading(true)
    setSelected(new Set())
    setDeletedCount(null)
    setLoadedCount(0)

    try {
      let accumulated: DuplicateKid[] = []
      let offset = 0
      let hasMore = true

      while (hasMore) {
        const { data: raw, error } = await supabase
          .from('kids')
          .select(`
            id, nom, prenom, date_naissance, n_national, created_at, user_id,
            users!kids_user_id_fkey(nom, prenom, email),
            registrations(id)
          `)
          .order('created_at', { ascending: true })
          .range(offset, offset + CHUNK_SIZE - 1)

        if (error) throw error

        const chunk: DuplicateKid[] = (raw || []).map((k: any) => ({
          id: k.id,
          nom: k.nom,
          prenom: k.prenom,
          date_naissance: k.date_naissance,
          n_national: k.n_national,
          created_at: k.created_at,
          user_id: k.user_id,
          parentLabel: k.users
            ? `${k.users.prenom || ''} ${k.users.nom || ''}`.trim() || k.users.email
            : k.user_id,
          registrations_count: k.registrations?.length || 0,
          canDelete: (k.registrations?.length || 0) === 0,
        }))

        accumulated = [...accumulated, ...chunk]
        setLoadedCount(accumulated.length)

        if (!raw || raw.length < CHUNK_SIZE) {
          hasMore = false
        } else {
          offset += CHUNK_SIZE
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      setAllKids(accumulated)
    } catch (err) {
      console.error('Error loading kids:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadKids()
  }, [loadKids])

  // Auto-expand first 5 groups, no auto-selection
  useEffect(() => {
    setExpanded(new Set(groups.slice(0, 5).map(g => g.key)))
    setSelected(new Set())
  }, [groups])

  const toggleFilter = (key: keyof Filters) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleSelect = (kidId: string, canDelete: boolean) => {
    if (!canDelete) return
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(kidId)) next.delete(kidId)
      else next.add(kidId)
      return next
    })
  }

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAllInGroup = (group: DuplicateGroup) => {
    const keepId = keeperOf(group)
    setSelected(prev => {
      const next = new Set(prev)
      group.kids.forEach(k => { if (k.id !== keepId && k.canDelete) next.add(k.id) })
      return next
    })
  }

  const deselectGroup = (group: DuplicateGroup) => {
    setSelected(prev => {
      const next = new Set(prev)
      group.kids.forEach(k => next.delete(k.id))
      return next
    })
  }

  const deleteSelected = async () => {
    const ids = Array.from(selected)
    setDeleting(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-duplicate-kid`
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kid_ids: ids }),
      })

      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Delete failed')
      setDeletedCount(json.deleted ?? 0)
    } catch (err) {
      console.error('Delete failed:', err)
    }

    setDeleting(false)
    setShowConfirm(false)
    await loadKids()
    onDeleted()
  }

  const activeFilterCount = Object.values(filters).filter(Boolean).length
  const totalSelected = selected.size

  return (
    <div className="fixed inset-0 bg-gray-900/60 z-50 flex items-start justify-end">
      <div className="w-full max-w-3xl h-full bg-white shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Nettoyage des doublons</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? 'Chargement...' : `${groups.length} groupe(s) détecté(s)`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
            <Filter className="h-3.5 w-3.5" />
            Critères :
          </div>
          {[
            { key: 'prenom' as keyof Filters, label: 'Même prénom' },
            { key: 'dateNaissance' as keyof Filters, label: 'Même date de naissance' },
            { key: 'nNational' as keyof Filters, label: 'Même numéro national' },
          ].map(({ key, label }) => (
            <label
              key={key}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border transition-colors select-none ${
                filters[key]
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                type="checkbox"
                checked={filters[key]}
                onChange={() => toggleFilter(key)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>

        {/* Success banner */}
        {deletedCount !== null && (
          <div className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex-shrink-0">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span><strong>{deletedCount}</strong> enfant(s) supprimé(s) avec succès.</span>
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">Chargement des données... {loadedCount > 0 && `(${loadedCount} enfants chargés)`}</span>
            </div>
          </div>
        ) : activeFilterCount === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <Filter className="h-12 w-12" />
              <p className="font-medium text-gray-600">Aucun critère sélectionné</p>
              <p className="text-sm">Cochez au moins un critère pour détecter les doublons.</p>
            </div>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-gray-500">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <p className="font-medium text-gray-700">Aucun doublon détecté</p>
              <p className="text-sm">Aucun enfant ne correspond aux critères sélectionnés.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="flex items-center gap-3 px-6 py-2 border-b border-gray-100 bg-gray-50 flex-shrink-0">
              <span className="text-xs text-gray-500">
                {groups.length} doublon(s) trouvé(s) · {groups.reduce((a, g) => a + g.kids.length, 0)} enfants concernés
              </span>
              {totalSelected > 0 && (
                <span className="ml-auto flex items-center gap-1.5 px-3 py-0.5 bg-red-600 text-white rounded-full text-xs font-medium">
                  {totalSelected} sélectionné(s)
                </span>
              )}
            </div>

            {/* Groups */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {groups.map(group => {
                const isExpanded = expanded.has(group.key)
                const groupSelectedCount = group.kids.filter(k => selected.has(k.id)).length
                const keepId = keeperOf(group)
                const deletableCount = group.kids.filter(k => k.canDelete && k.id !== keepId).length

                return (
                  <div key={group.key} className="rounded-xl border border-gray-200 overflow-hidden">
                    {/* Group header */}
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      onClick={() => toggleExpand(group.key)}
                    >
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">
                            {group.kids[0].prenom} {group.kids[0].nom}
                          </span>
                          <span className="text-xs font-medium text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                            x{group.kids.length}
                          </span>
                          <span className="text-xs text-gray-500">— {group.parentName}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {group.matchReasons.map(r => (
                            <span
                              key={r}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${REASON_COLOR[r] ?? 'bg-gray-100 text-gray-600'}`}
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {groupSelectedCount > 0 && (
                          <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-medium">
                            {groupSelectedCount} sél.
                          </span>
                        )}
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </button>

                    {/* Group body */}
                    {isExpanded && (
                      <div className="bg-white">
                        {deletableCount > 0 && (
                          <div className="flex gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/60">
                            <button
                              onClick={() => selectAllInGroup(group)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Sélectionner le(s) doublon(s) vide(s) ({deletableCount})
                            </button>
                            {groupSelectedCount > 0 && (
                              <>
                                <span className="text-gray-300">·</span>
                                <button
                                  onClick={() => deselectGroup(group)}
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Désélectionner
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        <div className="divide-y divide-gray-100">
                          {group.kids.map(kid => {
                            const isKeeper = kid.id === keepId
                            const isSelected = selected.has(kid.id)

                            return (
                              <div
                                key={kid.id}
                                onClick={() => toggleSelect(kid.id, kid.canDelete && !isKeeper)}
                                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                                  isSelected
                                    ? 'bg-red-50 cursor-pointer'
                                    : isKeeper
                                    ? 'bg-green-50'
                                    : kid.canDelete
                                    ? 'hover:bg-gray-50 cursor-pointer'
                                    : 'bg-gray-50/40'
                                }`}
                              >
                                {/* Checkbox */}
                                <div className="flex-shrink-0">
                                  {kid.canDelete && !isKeeper ? (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleSelect(kid.id, kid.canDelete)}
                                      onClick={e => e.stopPropagation()}
                                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                                    />
                                  ) : (
                                    <div className="h-4 w-4" />
                                  )}
                                </div>

                                {/* Avatar */}
                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <Baby className="h-4 w-4 text-gray-500" />
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {kid.prenom} {kid.nom}
                                    </span>
                                    {isKeeper && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                        Conserver
                                      </span>
                                    )}
                                    {isSelected && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                        À supprimer
                                      </span>
                                    )}
                                    {!kid.canDelete && (
                                      <span
                                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                                        title="A des inscriptions — non supprimable"
                                      >
                                        Protégé
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(kid.date_naissance)}
                                    </span>
                                    {kid.n_national && (
                                      <span className="flex items-center gap-1">
                                        <Hash className="h-3 w-3" />
                                        {kid.n_national}
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {kid.registrations_count} inscription(s)
                                    </span>
                                    <span>Créé le {formatDateTime(kid.created_at)}</span>
                                  </div>
                                </div>

                                {/* Status icon */}
                                <div className="flex-shrink-0">
                                  {!kid.canDelete ? (
                                    <ShieldAlert className="h-4 w-4 text-gray-400" />
                                  ) : isSelected ? (
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  ) : null}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <p className="text-sm text-gray-600">
                {totalSelected === 0
                  ? 'Sélectionnez des doublons à supprimer'
                  : `${totalSelected} enfant(s) sélectionné(s) pour suppression`}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={totalSelected === 0 || deleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer la sélection
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-gray-900/70 z-60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Vous allez supprimer <strong>{totalSelected} enfant(s)</strong> et toutes leurs données associées :
            </p>
            <ul className="text-sm text-gray-600 space-y-1 mb-6 pl-4 list-disc">
              <li>Informations de santé</li>
              <li>Allergies et régimes</li>
              <li>Restrictions d'activités</li>
              <li>Informations de départ</li>
              <li>Besoins d'inclusion</li>
            </ul>
            <p className="text-sm font-semibold text-red-700 mb-6">
              Cette action est irréversible.
            </p>

            {deleting && (
              <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Suppression en cours...
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={deleteSelected}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? 'Suppression...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
